import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { 
  Mail, 
  ArrowRight, 
  MessageSquare, 
  Chrome, 
  Sparkles, 
  CreditCard, 
  Key, 
  Play, 
  Command,
  HelpCircle,
  Zap,
  Globe,
  Clock,
  Languages,
  CheckCircle2
} from "lucide-react";

const Help = () => {
  const faqItems = [
    {
      question: "How many credits do I need per video?",
      answer: "Each video summary costs 1 credit, regardless of the video length.",
      icon: CreditCard
    },
    {
      question: "How do I get my API key?",
      answer: "After purchasing credits on our website, your API key will be displayed on the screen. You can also find it in your confirmation email and in your dashboard if you're logged in.",
      icon: Key
    },
    {
      question: "Can I use the same API key on multiple devices?",
      answer: "Yes, you can use your API key across our website, Telegram bot, Chrome extension, and API integrations. Credits are deducted from the same pool regardless of where you use the service.",
      icon: Globe
    },
    {
      question: "What happens if I run out of credits?",
      answer: "When you run out of credits, you'll need to purchase more to continue using the service. Visit our pricing page to get more credits.",
      icon: Zap
    },
    {
      question: "Do credits expire?",
      answer: "No, your credits never expire. They remain in your account until used.",
      icon: Clock
    },
    {
      question: "What types of videos can be summarized?",
      answer: "Our AI can summarize most YouTube videos with clear audio in English. Best results are typically from videos with spoken content like lectures, tutorials, podcasts, and news. Music videos, heavily visual content, or non-English videos may have limited results.",
      icon: Languages
    }
  ];

  const telegramCommands = [
    { command: "/start", description: "Get started with the bot" },
    { command: "/buy", description: "Purchase credits" },
    { command: "/apikey [key]", description: "Set your API key" },
    { command: "/quota", description: "Check your remaining credits" },
    { command: "/help", description: "Show help information" }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <Helmet>
        <title>Help & Support | RecapMate</title>
        <meta
          name="description"
          content="Get help with RecapMate - Find answers to frequently asked questions and learn how to use the YouTube and website AI summarization service."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 mb-6 rounded-full bg-primary/10 text-primary text-sm font-medium animate-in fade-in slide-in duration-500">
            <HelpCircle className="w-4 h-4 mr-2" />
            Help Center
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight animate-in fade-in slide-in duration-700">
            How Can We Help?
          </h1>
          
          <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto animate-in fade-in slide-in duration-900">
            Everything you need to know about using RecapMate to summarize content efficiently
          </p>
        </div>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Getting Started */}
          <Card className="shadow-lg hover-lift animate-in fade-in slide-in duration-500">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <CardTitle className="text-2xl">Getting Started</CardTitle>
              </div>
              <CardDescription className="text-base">
                RecapMate is an AI-powered service that creates concise, informative summaries of YouTube videos and websites
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-lg font-bold text-primary">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Purchase Credits</h3>
                    <p className="text-sm text-muted-foreground">
                      Visit our{" "}
                      <Link to="/buy-credits" className="text-primary hover:underline">
                        pricing page
                      </Link>{" "}
                      to get API credits
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-lg font-bold text-primary">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Get Your API Key</h3>
                    <p className="text-sm text-muted-foreground">
                      After purchase, your API key will be displayed and emailed to you
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-lg font-bold text-primary">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Start Summarizing</h3>
                    <p className="text-sm text-muted-foreground">
                      Use your API key with our web interface, Telegram bot, or Chrome extension
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Platform Guides */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Telegram Bot Guide */}
            <Card className="shadow-lg hover-lift animate-in fade-in slide-in duration-600">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-xl">Using the Telegram Bot</CardTitle>
                </div>
                <CardDescription>
                  Summarize videos directly in your Telegram chat
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {[
                    "Search for 'RecapMateBot' on Telegram",
                    "Get one free summary to try the service",
                    "Set up your API key with /apikey command",
                    "Send YouTube links to get summaries",
                    "Check remaining credits with /quota"
                  ].map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm">{step}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-sm">Bot Commands:</h4>
                  <div className="space-y-2">
                    {telegramCommands.map((cmd) => (
                      <div key={cmd.command} className="flex items-center gap-3 text-sm">
                        <code className="bg-background px-2 py-1 rounded font-mono text-xs">
                          {cmd.command}
                        </code>
                        <span className="text-muted-foreground">{cmd.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Chrome Extension Guide */}
            <Card className="shadow-lg hover-lift animate-in fade-in slide-in duration-700">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Chrome className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-xl">Using the Browser Extension</CardTitle>
                </div>
                <CardDescription>
                  Summarize videos directly from YouTube
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {[
                    "Install from Chrome Web Store",
                    "Configure your API key in extension options",
                    "See 'Summarize' button on YouTube videos",
                    "Right-click menu for quick access",
                    "View summaries directly on YouTube"
                  ].map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm">{step}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-sm">Extension Features:</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• One-click summarization</li>
                    <li>• Context menu integration</li>
                    <li>• In-page summary display</li>
                    <li>• Customizable summary options</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <Card className="shadow-lg animate-in fade-in slide-in duration-800">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {faqItems.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold mb-2">{item.question}</h3>
                          <p className="text-sm text-muted-foreground">
                            {item.answer}
                            {item.question.includes("run out of credits") && (
                              <>
                                {" Visit our "}
                                <Link to="/buy-credits" className="text-primary hover:underline">
                                  pricing page
                                </Link>
                                {" to get more credits."}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Contact Support */}
          <Card className="shadow-lg bg-gradient-primary text-white animate-in fade-in slide-in duration-900">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Still Need Help?</CardTitle>
              <CardDescription className="text-white/80 text-base mt-2">
                Our support team is here to assist you
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              <a
                href="mailto:support@recapmate.com"
                className="inline-flex items-center gap-2 text-lg font-medium hover:underline"
              >
                <Mail className="h-5 w-5" />
                support@recapmate.com
              </a>
              
              <div className="pt-4">
                <Button 
                  asChild 
                  size="lg" 
                  variant="secondary" 
                  className="hover-lift"
                >
                  <Link to="/buy-credits">
                    Get Started with RecapMate
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Help;